import React, { useState } from "react";
import { Link } from "react-router-dom";
import Spinner from "../components/Spinner";
import { SERVER_BASE_URL } from "../config";

function SignUpPage() {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
    });

    const [agreeTerms, setAgreeTerms] = useState(false);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: "",
        }));
        setErrorMessage("");
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const validate = () => {
        const newErrors = {};

        Object.keys(formData).forEach((field) => {
            if (!formData[field].trim()) {
                newErrors[field] = "This field is required.";
            }
        });

        if (!agreeTerms) {
            newErrors.agreeTerms = "You must agree to the terms and conditions.";
        }

        if (Object.keys(newErrors).length > 0) {
            return newErrors;
        }

        if (!formData.email.includes("@")) {
            newErrors.email = "Email must contain '@'.";
        }

        if (formData.password.length < 6) {
            newErrors.password = "Password must be at least 6 characters long.";
        }

        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match.";
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setErrors({});
        setSuccessMessage("");
        setErrorMessage("");
        setLoading(true);

        try {
            const response = await fetch(`${SERVER_BASE_URL}/api/users/register`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    password: formData.password,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                let detail;
                if (typeof errorData.detail === "string") {
                    detail = errorData.detail.replace(/^400:\s*/, "");
                } else if (Array.isArray(errorData.detail)) {
                    detail = errorData.detail.map(err => err.msg || JSON.stringify(err)).join(", ");
                } else {
                    detail = JSON.stringify(errorData.detail);
                }
                setErrorMessage(detail || "Registration failed.");
                return;
            }

            setSuccessMessage(
                "Registration successful! Check your email to verify your account."
            );
            setFormData({
                name: "",
                email: "",
                password: "",
                confirmPassword: "",
            });
            setAgreeTerms(false);
        } catch (error) {
            setErrorMessage("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 w-full max-w-md mx-4 my-8">
                <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Create Account</h2>
                    <p className="text-gray-600 mt-1">Sign up to get started</p>
                </div>
                
                {successMessage ? (
                    <div className="text-center">
                        <div className="p-4 bg-green-50 border border-green-200 rounded-md mb-6">
                            <p className="text-green-700">{successMessage}</p>
                        </div>
                        <Link
                            to="/login"
                            className="inline-block px-6 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 transition duration-200"
                        >
                            Go to Login
                        </Link>
                    </div>
                ) : (
                    <>
                        {errorMessage && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-4">
                                <p className="text-sm text-red-600">{errorMessage}</p>
                            </div>
                        )}
                        
                        <form onSubmit={handleSubmit} noValidate>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-2 border ${
                                        errors.name ? "border-red-500" : "border-gray-300"
                                    } rounded-md focus:ring-indigo-500 focus:border-indigo-500`}
                                    placeholder="Enter your name"
                                />
                                {errors.name && (
                                    <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                                )}
                            </div>
                            
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-2 border ${
                                        errors.email ? "border-red-500" : "border-gray-300"
                                    } rounded-md focus:ring-indigo-500 focus:border-indigo-500`}
                                    placeholder="Enter your email"
                                />
                                {errors.email && (
                                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                                )}
                            </div>
                            
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-2 border ${
                                            errors.password ? "border-red-500" : "border-gray-300"
                                        } rounded-md focus:ring-indigo-500 focus:border-indigo-500`}
                                        placeholder="Create a password"
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        onClick={togglePasswordVisibility}
                                    >
                                        {showPassword ? (
                                            <svg 
                                                xmlns="http://www.w3.org/2000/svg" 
                                                fill="none" 
                                                viewBox="0 0 24 24" 
                                                stroke="currentColor" 
                                                className="h-5 w-5 text-gray-500 hover:text-gray-700"
                                            >
                                                <path 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round" 
                                                    strokeWidth={2} 
                                                    d="M13.875 18.825C12.625 19.275 11.25 19.5 9.75 19.5c-6 0-9.75-4.5-9.75-9S3.75 1.5 9.75 1.5 19.5 6 19.5 10.5c0 1.5-.25 2.875-.7 4.125" 
                                                />
                                                <path 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round" 
                                                    strokeWidth={2} 
                                                    d="M16.5 17.25l3 3m0 0l3 3m-3-3h-6m6 0l-3-3" 
                                                />
                                            </svg>
                                        ) : (
                                            <svg 
                                                xmlns="http://www.w3.org/2000/svg" 
                                                fill="none" 
                                                viewBox="0 0 24 24" 
                                                stroke="currentColor" 
                                                className="h-5 w-5 text-gray-500 hover:text-gray-700"
                                            >
                                                <path 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round" 
                                                    strokeWidth={2} 
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
                                                />
                                                <path 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round" 
                                                    strokeWidth={2} 
                                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" 
                                                />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {errors.password && (
                                    <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                                )}
                            </div>
                            
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Confirm Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        className={`w-full px-4 py-2 border ${
                                            errors.confirmPassword ? "border-red-500" : "border-gray-300"
                                        } rounded-md focus:ring-indigo-500 focus:border-indigo-500`}
                                        placeholder="Confirm your password"
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        onClick={toggleConfirmPasswordVisibility}
                                    >
                                        {showConfirmPassword ? (
                                            <svg 
                                                xmlns="http://www.w3.org/2000/svg" 
                                                fill="none" 
                                                viewBox="0 0 24 24" 
                                                stroke="currentColor" 
                                                className="h-5 w-5 text-gray-500 hover:text-gray-700"
                                            >
                                                <path 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round" 
                                                    strokeWidth={2} 
                                                    d="M13.875 18.825C12.625 19.275 11.25 19.5 9.75 19.5c-6 0-9.75-4.5-9.75-9S3.75 1.5 9.75 1.5 19.5 6 19.5 10.5c0 1.5-.25 2.875-.7 4.125" 
                                                />
                                                <path 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round" 
                                                    strokeWidth={2} 
                                                    d="M16.5 17.25l3 3m0 0l3 3m-3-3h-6m6 0l-3-3" 
                                                />
                                            </svg>
                                        ) : (
                                            <svg 
                                                xmlns="http://www.w3.org/2000/svg" 
                                                fill="none" 
                                                viewBox="0 0 24 24" 
                                                stroke="currentColor" 
                                                className="h-5 w-5 text-gray-500 hover:text-gray-700"
                                            >
                                                <path 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round" 
                                                    strokeWidth={2} 
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
                                                />
                                                <path 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round" 
                                                    strokeWidth={2} 
                                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" 
                                                />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {errors.confirmPassword && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {errors.confirmPassword}
                                    </p>
                                )}
                            </div>
                            
                            <div className="flex items-start mb-4">
                                <div className="flex items-center h-5">
                                    <input
                                        type="checkbox"
                                        id="terms"
                                        checked={agreeTerms}
                                        onChange={(e) => setAgreeTerms(e.target.checked)}
                                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                    />
                                </div>
                                <div className="ml-3">
                                    <label htmlFor="terms" className="text-sm text-gray-600">
                                        I have read and agree to the{" "}
                                        <Link
                                            to="/terms"
                                            className="text-indigo-600 hover:text-indigo-800"
                                        >
                                            terms and conditions
                                        </Link>{" "}
                                        and{" "}
                                        <Link
                                            to="/privacy-policy"
                                            className="text-indigo-600 hover:text-indigo-800"
                                        >
                                            privacy policy
                                        </Link>
                                    </label>
                                    {errors.agreeTerms && (
                                        <p className="text-red-500 text-xs mt-1">{errors.agreeTerms}</p>
                                    )}
                                </div>
                            </div>
                            
                            <button
                                type="submit"
                                className={`w-full bg-indigo-600 text-white py-2 px-4 rounded-md font-medium flex items-center justify-center ${
                                    loading ? "opacity-70 cursor-not-allowed" : "hover:bg-indigo-700"
                                } transition duration-200`}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Spinner className="w-5 h-5 mr-2" />
                                        <span>Creating Account...</span>
                                    </>
                                ) : (
                                    "Create Account"
                                )}
                            </button>
                            
                            <div className="mt-4 text-center">
                                <p className="text-sm text-gray-600">
                                    Already have an account?{" "}
                                    <Link to="/login" className="text-indigo-600 hover:text-indigo-800 font-medium">
                                        Sign in
                                    </Link>
                                </p>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}

export default SignUpPage;